import { defineMessages } from 'react-intl';

/**
 * The copy of "Schedule all N posts" in one step (owner report 2026-10-10: "I just want to launch
 * my campaign"). The copy changes are recorded in
 * `docs/superpowers/design-reference/music24-rollout-plan/README.md`.
 */
export const launchMessages = defineMessages({
  scheduleAll: {
    id: 'app.sml.releases.rollout.launch.scheduleAll',
    defaultMessage: '{count, plural, one {Schedule # post} other {Schedule all # posts}}',
  },
  // The questions dialog.
  questionsTitle: {
    id: 'app.sml.releases.rollout.launch.questions.title',
    defaultMessage:
      '{count, plural, one {Before we schedule: # quick answer} other {Before we schedule: # quick answers}}',
  },
  questionsBody: {
    id: 'app.sml.releases.rollout.launch.questions.body',
    defaultMessage: 'We ask one time for the whole campaign. Your answer goes on every post of the channel.',
  },
  questionsConfirm: {
    id: 'app.sml.releases.rollout.launch.questions.confirm',
    defaultMessage: '{count, plural, one {Schedule # post} other {Schedule # posts}}',
  },
  questionsCancel: { id: 'app.sml.releases.rollout.launch.questions.cancel', defaultMessage: 'Not now' },
  questionPosts: {
    id: 'app.sml.releases.rollout.launch.questions.posts',
    defaultMessage: '{name} · {count, plural, one {# post} other {# posts}}',
  },
  youtubeRemember: {
    id: 'app.sml.releases.rollout.launch.questions.youtubeRemember',
    defaultMessage:
      'We select who can see the videos for your next YouTube posts too. We ask "Made for kids" for each campaign.',
  },
  tiktokEachTime: {
    id: 'app.sml.releases.rollout.launch.questions.tiktokEachTime',
    defaultMessage: 'TikTok wants you to choose this yourself, so we ask for each campaign and keep no answer.',
  },
  tiktokLater: {
    id: 'app.sml.releases.rollout.launch.questions.tiktokLater',
    defaultMessage:
      'TikTok cannot take the answer now. The TikTok posts stay drafts, and the other posts are scheduled.',
  },
  // The summary.
  allScheduledTitle: {
    id: 'app.sml.releases.rollout.launch.done.title',
    defaultMessage: 'Your campaign is scheduled',
  },
  allScheduledBody: {
    id: 'app.sml.releases.rollout.launch.done.body',
    defaultMessage:
      '{count, plural, one {# post goes out at its time.} other {# posts go out at their times.}} You can change a post until it goes out.',
  },
  // The summary splits the posts by how they go out (QA finding F02): by themselves, or as reminders.
  allRemindersTitle: {
    id: 'app.sml.releases.rollout.launch.done.remindersTitle',
    defaultMessage: 'Your reminders are set',
  },
  splitBody: {
    id: 'app.sml.releases.rollout.launch.done.splitBody',
    defaultMessage:
      '{auto, plural, =0 {} one {# post goes out by itself at its time.} other {# posts go out by themselves at their times.}} {manual, plural, =0 {} one {# post comes to you as a "post it yourself" reminder.} other {# posts come to you as "post it yourself" reminders.}} You can change a post until it goes out.',
  },
  remindersHint: {
    id: 'app.sml.releases.rollout.launch.done.remindersHint',
    defaultMessage: 'Connect a channel, and its posts go out by themselves.',
  },
  connectChannel: { id: 'app.sml.releases.rollout.launch.connectChannel', defaultMessage: 'Connect a channel' },
  partTitle: {
    id: 'app.sml.releases.rollout.launch.part.title',
    defaultMessage: '{scheduled} scheduled. {left, plural, one {# needs you:} other {# need you:}}',
  },
  noneTitle: {
    id: 'app.sml.releases.rollout.launch.none.title',
    defaultMessage: 'Nothing is scheduled yet. {left, plural, one {# post needs you:} other {# posts need you:}}',
  },
  dismiss: { id: 'app.sml.releases.rollout.launch.dismiss', defaultMessage: 'Close' },
  rowName: { id: 'app.sml.releases.rollout.launch.row.name', defaultMessage: '{step} · {name}' },
  rowNameNoStep: { id: 'app.sml.releases.rollout.launch.row.nameNoStep', defaultMessage: 'A post on {name}' },
  // The reasons, in plain words.
  reasonNeedsAnswer: {
    id: 'app.sml.releases.rollout.launch.reason.needsAnswer',
    defaultMessage: '{name} needs your answer: who can see the post.',
  },
  reasonReconnect: {
    id: 'app.sml.releases.rollout.launch.reason.reconnect',
    defaultMessage: 'Your {name} channel is signed out.',
  },
  reasonMediaMissing: {
    id: 'app.sml.releases.rollout.launch.reason.mediaMissing',
    defaultMessage: '{name} needs a photo or a video, and this post has none.',
  },
  reasonMediaRefused: {
    id: 'app.sml.releases.rollout.launch.reason.mediaRefused',
    defaultMessage: '{name} does not take this file.',
  },
  reasonMonthLimit: {
    id: 'app.sml.releases.rollout.launch.reason.monthLimit',
    defaultMessage: 'Your plan has no scheduled posts left this month.',
  },
  reasonChannelLimit: {
    id: 'app.sml.releases.rollout.launch.reason.channelLimit',
    defaultMessage: 'Your plan does not include the {name} channel.',
  },
  reasonTimePast: {
    id: 'app.sml.releases.rollout.launch.reason.timePast',
    defaultMessage: 'Its time is in the past. Pick a new time.',
  },
  reasonTikTokWatermark: {
    id: 'app.sml.releases.rollout.launch.reason.tiktokWatermark',
    defaultMessage: 'TikTok does not take a file with the MUSIC24 mark of the Free plan.',
  },
  reasonXMonthLimit: {
    id: 'app.sml.releases.rollout.launch.reason.xMonthLimit',
    defaultMessage: 'Your X posts for this month are used. Post it by hand, or give it a time next month.',
  },
  // The fixes: one button each.
  fixAnswer: { id: 'app.sml.releases.rollout.launch.fix.answer', defaultMessage: 'Answer' },
  fixReconnect: { id: 'app.sml.releases.rollout.launch.fix.reconnect', defaultMessage: 'Reconnect' },
  fixOpenPost: { id: 'app.sml.releases.rollout.launch.fix.openPost', defaultMessage: 'Open the post' },
  fixPlans: { id: 'app.sml.releases.rollout.launch.fix.plans', defaultMessage: 'See plans' },
  scheduleWhatFits: {
    id: 'app.sml.releases.rollout.launch.scheduleWhatFits',
    defaultMessage: 'Schedule only the {count, plural, one {# post} other {# posts}} that fit',
  },
});
